import { HoverCard } from "#components/ui/HoverCard.tsx";
import { MarkdownLink } from "#components/ui/markdown/Markdown.tsx";
import { MentionTokenContent } from "#features/conversations/components/conversation-input/MentionToken.tsx";
import { MentionMemberCard } from "#features/conversations/components/conversation-user/MentionMemberCard.tsx";
import { useMemberCardByEmail } from "#features/conversations/hooks/useMemberCard.ts";
import { useWlAppLinks } from "#features/conversations/hooks/useWlAppLinks.ts";
import { useHoverCard } from "#hooks/useHoverCard.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent, MixpanelLinkType, MixpanelSourceLocation } from "#types/tracking.ts";
import { useCallback, useId, useRef } from "react";
import { Button, type PressEvent } from "react-aria-components";

interface Props {
    href: string;
    userName: string;
    email: string;
    disabled: boolean;
    conversationId: string;
}

const useTrackPress = (conversationId: string) => {
    const { trackEvent } = useTracking();

    return useCallback(() => {
        trackEvent(MixpanelEvent.AssistantSourceClicked, {
            Type: MixpanelLinkType.User,
            "Conversation Id": conversationId,
            Location: MixpanelSourceLocation.Inline
        });
    }, [conversationId, trackEvent]);
};

// A mention chip opening a mention's card, so a person looks and acts the same whoever named them. Pressing it opens the
// card rather than the mail app; the card's email writes to them. In the unified assistant, the card of a person once
// found links to their wl-app page, as a sent mention's does.
const AssistantMessageUserWithCard = ({ userName, email, disabled, conversationId }: Props) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const cardRef = useRef<HTMLElement>(null);
    const cardId = useId();
    // Looked up once the answer is written, with every other person the conversation names, so the photo needs no hover.
    const { data: member, isLoading } = useMemberCardByEmail(email, { isEnabled: !disabled });
    const { linksToProfile } = useWlAppLinks();
    const { isOpen, triggerProps, popoverProps, cardProps } = useHoverCard({ triggerRef, cardRef, isDisabled: disabled, opensOnPress: true });
    const trackPress = useTrackPress(conversationId);

    // Where the card links the person's page, the source is clicked when that link is, never when the chip opens the
    // card, whether or not the lookup has answered yet.
    const onPress = (event: PressEvent) => {
        triggerProps.onPress(event);
        if (!linksToProfile) {
            trackPress();
        }
    };

    return (
        <>
            <Button
                ref={triggerRef}
                className="wai-mention-token"
                data-private=""
                aria-describedby={isOpen ? cardId : undefined}
                {...triggerProps}
                onPress={onPress}
            >
                <MentionTokenContent name={userName} imageUrl={member?.imageUrl} copiesWithAt={false} />
            </Button>
            <HoverCard triggerRef={triggerRef} cardRef={cardRef} cardId={cardId} label={userName} className="wai-mention-hover-card" isPrivate popoverProps={popoverProps} cardProps={cardProps}>
                <MentionMemberCard
                    memberId={member?.id}
                    fullName={userName}
                    imageUrl={member?.imageUrl}
                    jobTitle={member?.jobTitle}
                    isLoading={isLoading}
                    isNotFound={member === null}
                    linksToProfile={linksToProfile}
                    onPageLinkPress={trackPress}
                />
            </HoverCard>
        </>
    );
};

export const AssistantMessageUser = (props: Props) => {
    // A bare address ("write to jane@x.com") shows an address, not a person: a chip would read the address as their name.
    const { href, userName, email } = props;

    return userName.toLowerCase() === email.toLowerCase()
        ? <MarkdownLink href={href}>{userName}</MarkdownLink>
        : <AssistantMessageUserWithCard {...props} />;
};
